import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TabAbonnement.module.css';
import MetaHead from '../components/MetaHead';
import { getPlanById, buildPlans } from '../constants/plans';
import { formatDate, formatStripeAmount, formatPrice, formatPriceParts } from '../utils/format';

/* ============================================================
   TabAbonnement — onglet "Abonnement"
   ------------------------------------------------------------
   IDs DOM préservés pour le backend Express :
     #ab-plan-name        ← nom du plan actuel
     #ab-plan-status      ← badge "Actif" / "Chargement…"
     .ab-tier (×3)        ← cartes tarifaires (data-plan)
     .ab-tier-badge       ← bandeau "Votre plan"
     .ab-choose-btn       ← bouton "Choisir" (data-plan)

   Callbacks (props) :
     - onSubscribe(plan)      ← abSouscrire(plan, btn) (Stripe Checkout)
     - onOpenPortal()         ← ouvre le Stripe Billing Portal (carte + factures)
     - onCancelSubscription() ← résilie en fin de période, sans quitter Revyo
     - onResumeSubscription() ← annule une résiliation programmée
     - onContactSupport()  ← mailto handler

   Données (props) :
     - invoices            ← [{ id, number, amount (centimes), currency,
                                status, created (ts UNIX sec),
                                pdf_url, hosted_invoice_url }]
     - invoicesLoading     ← bool, état de chargement des factures
     - planStatusRaw       ← statut Stripe brut (active / canceled / past_due…)
     - canOpenPortal       ← bool, true si user a un stripe_customer_id
     - portalLoading       ← bool, true pendant POST /portal-session
     - cancelAtPeriodEnd   ← bool, résiliation programmée (abonnement encore actif)
     - currentPeriodEnd    ← ISO, fin de la période payée (prochaine facture OU fin d'accès)
     - cancelLoading       ← bool, true pendant POST cancel/resume-subscription

   Source unique des plans : constants/plans.js (PLANS / PLAN_BY_ID)
   ============================================================ */

// Mappage statut Stripe → clé i18n + classe CSS pour le badge facture
const INVOICE_STATUS_MAP = {
  paid:          { labelKey: 'abonnement.invoice_status_paid',          cls: 'green' },
  open:          { labelKey: 'abonnement.invoice_status_open',          cls: 'orange' },
  uncollectible: { labelKey: 'abonnement.invoice_status_uncollectible', cls: 'red' },
  void:          { labelKey: 'abonnement.invoice_status_void',          cls: 'gray' },
  draft:         { labelKey: 'abonnement.invoice_status_draft',         cls: 'gray' },
};

export default function TabAbonnement({
  arrivalNotice = null,
  currentPlan = null,
  planStatus = null,
  planStatusRaw = null,
  invoices = [],
  invoicesLoading = false,
  canOpenPortal = false,
  portalLoading = false,
  cancelAtPeriodEnd = false,
  currentPeriodEnd = null,
  cancelLoading = false,
  onSubscribe = () => {},
  onOpenPortal = () => {},
  onCancelSubscription = () => {},
  onResumeSubscription = () => {},
  onContactSupport = () => {},
}) {
  const { t, i18n } = useTranslation('tabs');
  const PLANS = buildPlans(t);
  const lang = i18n.language || 'fr';
  const [loading, setLoading] = useState(null);
  // Si currentPlan est null/undefined/inconnu → empty state plus bas (pas de fallback "pro").
  // ⚠️ getPlanById (et non PLAN_IDS/buildPlans) : un abonné LEGACY 'essentiel'
  // n'a plus de carte dans la grille publique, mais son bandeau « Votre plan
  // actuel » doit afficher « Essentiel — 69 € ». getPlanById résout aussi les
  // plans legacy ET fournit le label traduit (PLAN_BY_ID seul n'a pas de label).
  const current = currentPlan ? getPlanById(currentPlan, t) : null;
  // Modale de confirmation de résiliation : action irréversible sur un produit
  // payant, elle mérite mieux qu'un window.confirm système.
  const [confirmCancel, setConfirmCancel] = useState(false);

  // Date de fin de période, formatée dans la langue de l'interface. Remplace le
  // « 1er mai 2026 » qui était codé en dur et affiché à tous les abonnés.
  const periodEndLabel = currentPeriodEnd ? formatDate(currentPeriodEnd, lang) : null;

  // Trois états visuels distincts pour le badge, là où la classe « active »
  // (verte) était appliquée en dur quel que soit le statut réel.
  const statusVariant =
    cancelAtPeriodEnd ? 'canceling'
    : (planStatusRaw === 'canceled' || planStatusRaw === 'unpaid') ? 'ended'
    : (planStatusRaw === 'past_due') ? 'warning'
    : 'active';
  const STATUS_CLASS = {
    active:    styles.statusActive,
    canceling: styles.statusCanceling,
    warning:   styles.statusCanceling,
    ended:     styles.statusEnded,
  };

  const handleChoose = async (planId) => {
    if (planId === currentPlan) return;
    setLoading(planId);
    try {
      await onSubscribe(planId);
    } finally {
      setTimeout(() => setLoading(null), 800);
    }
  };

  return (
    <div className={styles.wrap}>
      <MetaHead page="abonnement" />

      {/* Bandeau d'explication quand l'utilisateur est ARRIVÉ ICI par une
          redirection (402, checkout annulé ou échoué) plutôt qu'en cliquant
          sur l'onglet. Sans lui, la page s'ouvre sans que rien ne dise
          pourquoi l'accès au dashboard a été refusé. */}
      {arrivalNotice && (
        <div className={styles.notice} role="status">
          <span className={styles.noticeIcon} aria-hidden="true">
            {arrivalNotice === 'payment_cancelled' ? '↩️' : '💳'}
          </span>
          <div>
            <div className={styles.noticeTitle}>{t(`abonnement.notices.${arrivalNotice}.title`)}</div>
            <div className={styles.noticeText}>{t(`abonnement.notices.${arrivalNotice}.text`)}</div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1>
            <span className={styles.titleEmoji}>{t('abonnement.title_emoji')}</span>
            {t('abonnement.title')}
          </h1>
          <p>{t('abonnement.subtitle')}</p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={onContactSupport}
          >
            {t('abonnement.contact_support')}
          </button>
        </div>
      </header>

      {/* PLAN ACTUEL — bandeau hero (ou empty state si pas de plan actif) */}
      {current ? (
        <section className={styles.currentPlan}>
          <div className={styles.currentLeft}>
            <div className={styles.currentLabel}>{t('abonnement.current_label')}</div>
            <div className={styles.currentName} id="ab-plan-name">
              <span className={styles.currentEmoji}>{current.emoji}</span>
              {current.label}
            </div>
            <div className={styles.currentMeta}>
              <span className={styles.currentPrice}>{formatPrice(current.price, lang)}</span>
              <span className={styles.currentSub}>{t('abonnement.current_price_per_month')}</span>
            </div>
          </div>
          <div className={styles.currentRight}>
            <span
              className={`${styles.statusBadge} ${STATUS_CLASS[statusVariant] || styles.statusActive}`}
              id="ab-plan-status"
            >
              ● {cancelAtPeriodEnd
                  ? t('abonnement.status_canceling')
                  : (planStatus || t('abonnement.current_status_active'))}
            </span>
            {/* Selon l'état : prochaine facture (l'abonnement continue) ou date de
                fin d'accès (résiliation programmée). Plus de date en dur. */}
            {periodEndLabel && (
              <div className={styles.currentSavings}>
                {cancelAtPeriodEnd
                  ? <>{t('abonnement.access_until_label')} <strong>{periodEndLabel}</strong></>
                  : <>{t('abonnement.next_invoice_label')} <strong>{periodEndLabel}</strong></>}
              </div>
            )}
          </div>
          <div className={styles.currentBlob} aria-hidden="true" />
        </section>
      ) : (
        <section className={styles.currentPlan} id="ab-plan-name">
          <div className={styles.currentLeft}>
            <div className={styles.currentLabel}>{t('abonnement.empty_label')}</div>
            <div className={styles.currentName}>
              <span className={styles.currentEmoji}>🌱</span>
              {t('abonnement.empty_title')}
            </div>
            <div className={styles.currentMeta}>
              <span className={styles.currentSub}>
                {t('abonnement.empty_sub')}
              </span>
            </div>
          </div>
          <div className={styles.currentRight}>
            <span
              className={`${styles.statusBadge}`}
              id="ab-plan-status"
            >
              ● {t('abonnement.current_status_inactive')}
            </span>
          </div>
          <div className={styles.currentBlob} aria-hidden="true" />
        </section>
      )}

      {/* RÉSILIATION PROGRAMMÉE — bandeau explicite.
          Avant ce chantier, un abonné qui venait de résilier ne voyait
          strictement rien : la page continuait d'afficher « Actif ». */}
      {cancelAtPeriodEnd && (
        <section className={styles.cancelNotice} role="status">
          <div className={styles.cancelNoticeLeft}>
            <div className={styles.cancelNoticeIcon} aria-hidden="true">🔔</div>
            <div>
              <div className={styles.cancelNoticeTitle}>
                {t('abonnement.cancel_notice_title')}
              </div>
              <div className={styles.cancelNoticeSub}>
                {periodEndLabel
                  ? t('abonnement.cancel_notice_sub', { date: periodEndLabel })
                  : t('abonnement.cancel_notice_sub_nodate')}
              </div>
            </div>
          </div>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onResumeSubscription}
            disabled={cancelLoading}
          >
            {cancelLoading ? t('abonnement.cancel_loading') : t('abonnement.resume_button')}
          </button>
        </section>
      )}

      {/* GRILLE 3 OFFRES */}
      <section className={styles.tiersGrid}>
        {PLANS.map((plan) => {
          const isCurrent = plan.id === currentPlan;
          const isLoading = loading === plan.id;
          return (
            <article
              key={plan.id}
              className={`${styles.tier} ab-tier ${plan.accent ? styles.tierAccent : ''} ${isCurrent ? styles.tierCurrent : ''}`}
              data-plan={plan.id}
            >
              {plan.popular && !isCurrent && (
                <div className={styles.popularRibbon}>{t('abonnement.tier_popular')}</div>
              )}
              {isCurrent && (
                <div
                  className={`${styles.tierBadge} ab-tier-badge`}
                  style={{ display: 'inline-block' }}
                >
                  {t('abonnement.tier_current_badge')}
                </div>
              )}

              <div className={styles.tierEmoji}>{plan.emoji}</div>
              <div className={styles.tierName}>{plan.label}</div>
              <div className={styles.tierPrice}>
                {/* Prix formaté par Intl : « 9,99 » en fr/es/it, « 9.99 » en en,
                    et le symbole placé AVANT ou APRÈS selon la locale (€9.99 en
                    anglais). On garde deux spans pour conserver la typo du design
                    (nombre 42px / symbole 22px) — d'où formatPriceParts plutôt
                    qu'une string unique. La clé i18n `tier_currency` n'est plus
                    utilisée : le symbole vient d'Intl. */}
                {(() => {
                  const { value, currency, currencyFirst } = formatPriceParts(plan.price, lang);
                  return currencyFirst ? (
                    <>
                      <span className={styles.tierCurrency}>{currency}</span>
                      {value}
                    </>
                  ) : (
                    <>
                      {value}
                      <span className={styles.tierCurrency}>{currency}</span>
                    </>
                  );
                })()}
                <span className={styles.tierPeriod}>{t('abonnement.tier_period')}</span>
              </div>
              <div className={styles.tierTagline}>{plan.tagline}</div>

              <ul className={styles.tierFeatures}>
                {plan.features.map((f, i) => (
                  <li key={`${plan.id}-feat-${i}`}>
                    <span className={styles.tierCheck}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className={`${styles.tierBtn} ab-choose-btn ${isCurrent ? styles.tierBtnDisabled : ''}`}
                data-plan={plan.id}
                onClick={() => handleChoose(plan.id)}
                disabled={isCurrent || isLoading}
              >
                {isCurrent ? t('abonnement.tier_btn_current') : isLoading ? t('abonnement.tier_btn_loading') : t('abonnement.tier_btn_choose')}
              </button>
            </article>
          );
        })}
      </section>

      {/* PORTAIL STRIPE — visible uniquement si user a un abonnement Stripe */}
      {canOpenPortal && (
        <section className={styles.portalCard}>
          <div className={styles.portalLeft}>
            <div className={styles.portalIcon}>💳</div>
            <div>
              <div className={styles.portalTitle}>{t('abonnement.portal_title')}</div>
              <div className={styles.portalSub}>
                {t('abonnement.portal_sub')}
              </div>
            </div>
          </div>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={onOpenPortal}
            disabled={portalLoading}
          >
            {portalLoading ? t('abonnement.portal_button_loading') : t('abonnement.portal_button')}
          </button>
        </section>
      )}

      {/* RÉSILIATION — depuis Revyo, sans passer par le portail Stripe.
          Masquée si la résiliation est déjà programmée (le bandeau du haut
          porte alors l'action inverse) ou si l'abonnement est déjà terminé. */}
      {canOpenPortal && !cancelAtPeriodEnd && planStatusRaw !== 'canceled' && (
        <section className={styles.cancelCard}>
          <div className={styles.portalLeft}>
            <div>
              <div className={styles.cancelCardTitle}>{t('abonnement.cancel_title')}</div>
              <div className={styles.portalSub}>{t('abonnement.cancel_sub')}</div>
            </div>
          </div>
          <button
            type="button"
            className={styles.btnDanger}
            onClick={() => setConfirmCancel(true)}
            disabled={cancelLoading}
          >
            {t('abonnement.cancel_button')}
          </button>
        </section>
      )}

      {/* Modale de confirmation — action irréversible sur un produit payant. */}
      {confirmCancel && (
        <div
          className={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ab-cancel-title"
          onClick={() => { if (!cancelLoading) setConfirmCancel(false); }}
        >
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTitle} id="ab-cancel-title">
              {t('abonnement.cancel_modal_title')}
            </div>
            <p className={styles.modalText}>
              {periodEndLabel
                ? t('abonnement.cancel_modal_text', { date: periodEndLabel })
                : t('abonnement.cancel_modal_text_nodate')}
            </p>
            <p className={styles.modalText}>{t('abonnement.cancel_modal_reassure')}</p>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={() => setConfirmCancel(false)}
                disabled={cancelLoading}
              >
                {t('abonnement.cancel_modal_keep')}
              </button>
              <button
                type="button"
                className={styles.btnDanger}
                onClick={async () => {
                  await onCancelSubscription();
                  setConfirmCancel(false);
                }}
                disabled={cancelLoading}
              >
                {cancelLoading ? t('abonnement.cancel_loading') : t('abonnement.cancel_modal_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HISTORIQUE FACTURES */}
      <section className={styles.invoicesCard}>
        <header className={styles.invoicesHeader}>
          <div>
            <div className={styles.invoicesTitle}>{t('abonnement.invoices_title')}</div>
            <div className={styles.invoicesSub}>
              {t('abonnement.invoices_sub')}
            </div>
          </div>
          {!invoicesLoading && invoices.length > 0 && (
            <span className={styles.invoicesBadge}>
              {t('abonnement.invoices_badge', { count: invoices.length })}
            </span>
          )}
        </header>

        {invoicesLoading ? (
          <div className={styles.invoicesEmpty}>
            <div className={styles.invoicesEmptyIcon}>⏳</div>
            <div className={styles.invoicesEmptyTitle}>{t('abonnement.invoices_loading_title')}</div>
          </div>
        ) : invoices.length === 0 ? (
          <div className={styles.invoicesEmpty}>
            <div className={styles.invoicesEmptyIcon}>📄</div>
            <div className={styles.invoicesEmptyTitle}>{t('abonnement.invoices_empty_title')}</div>
            <div className={styles.invoicesEmptySub}>
              {t('abonnement.invoices_empty_sub')}
            </div>
          </div>
        ) : (
          <div className={styles.invoicesTable} role="table">
            <div className={styles.invoiceHead} role="row">
              <span>{t('abonnement.invoices_col_date')}</span>
              <span>{t('abonnement.invoices_col_description')}</span>
              <span>{t('abonnement.invoices_col_amount')}</span>
              <span>{t('abonnement.invoices_col_status')}</span>
              <span></span>
            </div>
            {invoices.map((inv) => {
              const statusInfo = INVOICE_STATUS_MAP[inv.status] || { labelKey: null, cls: 'gray' };
              const statusLabel = statusInfo.labelKey ? t(statusInfo.labelKey) : inv.status;
              // Date formatée selon la locale : "12 mars 2026" / "12 March 2026" / etc.
              const dateStr = inv.created
                ? formatDate(new Date(inv.created * 1000), lang, { day: '2-digit', month: 'long', year: 'numeric' })
                : '';
              const amountStr = formatStripeAmount(inv.amount, inv.currency, lang);
              const label = inv.number
                ? t('abonnement.invoice_label_with_number', { number: inv.number })
                : t('abonnement.invoice_label_default');
              return (
                <div className={styles.invoiceRow} role="row" key={inv.id}>
                  <span className={styles.invoiceDate}>{dateStr}</span>
                  <span className={styles.invoiceLabel}>{label}</span>
                  <span className={styles.invoiceAmount}>{amountStr}</span>
                  <span className={`${styles.invoiceStatus} ${styles[`status_${statusInfo.cls}`] || ''}`}>
                    ● {statusLabel}
                  </span>
                  {inv.hosted_invoice_url ? (
                    <a
                      href={inv.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.invoiceDl}
                      aria-label={t('abonnement.invoice_open_label', { date: dateStr })}
                    >
                      {t('abonnement.invoice_open')}
                    </a>
                  ) : (
                    <span className={styles.invoiceDl} aria-disabled="true" style={{ opacity: 0.4 }}>
                      —
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
